import { useCallback } from 'react';

import { getGTMConfig } from '@/data/events/handlers/gtm';
import { isB2BStore, useSettings } from '@/data/Settings';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { B2B, B2C } from 'presentation/events/constants/eventConstants';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { triggerGA4EcommerceEvent } from 'presentation/events/services/ga4EcommerceService';
import { SendSelectItemEventProps } from './types';
import { buildGA4SelectItemEventPayload } from './utils';
import { GA4ChatbotEventParams } from 'presentation/events/types/ga4Types';
import { GA4_BOT_CHANNEL_MOBILE } from 'presentation/events/hooks/useTrackDibot';

export const useTrackSelectItemGA4 = () => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { isMobileApp } = useMobileApp();
	const isB2B = isB2BStore(settings);

	return useCallback(
		({
			product,
			isEmarsys,
			categories,
			quantity,
			chatbotEventParams,
			plpBadges,
		}: SendSelectItemEventProps) => {
			const { ga4 } = getGTMConfig(settings);

			if (!ga4 || !physicalStoreId) return;

			const payload = buildGA4SelectItemEventPayload({
				isEmarsys,
				settings,
				product,
				categories,
				physicalStoreId,
				quantity,
				plpBadges,
			});

			const { giftType } = product;
			if (giftType) {
				payload.ecommerce.items = payload.ecommerce.items.map((item) => ({
					...item,
					gift_type: giftType,
				}));
			}

			if (!isMobileApp) {
				triggerGA4EcommerceEvent(payload, { cleanup: true, eventParams: chatbotEventParams });
				return;
			}

			const chatbotEvntParams: GA4ChatbotEventParams =
				chatbotEventParams.chatbot_event === 'true'
					? { ...chatbotEventParams, channel: GA4_BOT_CHANNEL_MOBILE }
					: chatbotEventParams;

			const mobilePayload = {
				...payload.ecommerce,
				app_thd: !isB2B ? B2C : B2B,
				...chatbotEvntParams,
			};

			logEventWithValues(payload.event, mobilePayload);
		},
		[isB2B, isMobileApp, physicalStoreId, settings]
	);
};
